const CHANNEL_PARAMS_STORAGE_KEY = "WEB_FUNNEL_CHANNEL_PARAMS";
// localStorage 와 같은 값을 쿠키에도 둔다. 인스타그램 등 인앱 브라우저(비영구 WKWebView)는 카카오 로그인으로
// 앱을 오가는 사이 웹 프로세스가 회수되면 메모리에만 있던 localStorage 가 비는데, 쿠키는 별도 저장소라 살아남는다
// (같은 세션에서 Mixpanel 쿠키가 유지된 것으로 확인). metaCapi 의 _fbc 와 같은 1st-party 쿠키 규칙.
const CHANNEL_PARAMS_COOKIE_KEY = "WEB_FUNNEL_CHANNEL_PARAMS";
const CHANNEL_PARAMS_COOKIE_DAYS = 90;

// utm 5종 + utm_id + 광고 클릭 ID. Google은 iOS(ATT)에서 gclid 대신 wbraid/gbraid를 붙이고,
// gad_source/gad_campaignid는 utm 미세팅 캠페인에서도 항상 붙는 자동 파라미터다.
const CHANNEL_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "fbclid",
  "gclid",
  "wbraid",
  "gbraid",
  "gad_source",
  "gad_campaignid",
] as const;

export type ChannelParamKey = (typeof CHANNEL_PARAM_KEYS)[number];

export type ChannelParams = Partial<Record<ChannelParamKey, string>>;

export interface StoredChannelParams {
  params: ChannelParams;
  capturedAt: number;
}

// 랜딩 URL의 유입 채널 파라미터를 localStorage에 저장한다 (last-click 덮어쓰기).
// 파라미터가 하나도 없으면 기존 저장값을 유지한다 — OAuth 왕복·재진입 시 소실 방지.
export function captureChannelParamsFromUrl(
  search: string = window.location.search
): void {
  try {
    const searchParams = new URLSearchParams(search);
    const params: ChannelParams = {};
    CHANNEL_PARAM_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    });

    if (Object.keys(params).length === 0) return;

    const serialized = JSON.stringify({ params, capturedAt: Date.now() });
    // 두 저장소는 독립적으로 시도한다 — 한쪽이 막혀도 다른 쪽은 남긴다
    try {
      localStorage.setItem(CHANNEL_PARAMS_STORAGE_KEY, serialized);
    } catch {}
    try {
      setCookie(CHANNEL_PARAMS_COOKIE_KEY, serialized);
    } catch {}
  } catch {}
}

// 둘 중 있는 쪽을, 둘 다 있으면 최근에 캡처된 쪽을 쓴다 (last-click 유지)
export function getStoredChannelParams(): StoredChannelParams | undefined {
  const fromStorage = parseStored(readLocalStorage());
  const fromCookie = parseStored(readCookie());
  if (fromStorage && fromCookie) {
    return fromCookie.capturedAt > fromStorage.capturedAt
      ? fromCookie
      : fromStorage;
  }
  return fromStorage ?? fromCookie;
}

function parseStored(raw: string | undefined): StoredChannelParams | undefined {
  if (!raw) return undefined;
  try {
    const stored = JSON.parse(raw);
    if (
      !stored ||
      typeof stored !== "object" ||
      !stored.params ||
      typeof stored.params !== "object" ||
      typeof stored.capturedAt !== "number"
    ) {
      return undefined;
    }
    return stored as StoredChannelParams;
  } catch {
    return undefined;
  }
}

function readLocalStorage(): string | undefined {
  try {
    return localStorage.getItem(CHANNEL_PARAMS_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function readCookie(): string | undefined {
  try {
    if (typeof document === "undefined") return undefined;
    const match = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(CHANNEL_PARAMS_COOKIE_KEY + "="));
    if (!match) return undefined;
    return decodeURIComponent(
      match.slice(CHANNEL_PARAMS_COOKIE_KEY.length + 1)
    );
  } catch {
    return undefined;
  }
}

function setCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const expires = new Date();
  expires.setDate(expires.getDate() + CHANNEL_PARAMS_COOKIE_DAYS);
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`;
}
